import React from "react";
import { useLocation } from "react-router-dom";
import { Menu as MenuIcon } from "lucide-react";

import NotificationBell from "@/components/navbar/NotificationBell";
import PropertySelector from "@/components/property-selector/PropertySelector";
import type { MultiSelectPage } from "@/redux/slices/property";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import breakpoints from "@/theme/breakpoints";
import { SIDEBAR, HEADER } from "@/design-system/tokens";
import { cn } from "@/utils/cn";

const MENU_ICON_SIZE = 22;

type DrawerMenuButtonProps = {
  onClick: React.MouseEventHandler<HTMLElement>;
  hoverClass: string;
  className?: string;
};

function DrawerMenuButton({
  onClick,
  hoverClass,
  className = "",
}: DrawerMenuButtonProps) {
  return (
    <button
      type="button"
      aria-label="Open drawer"
      onClick={onClick}
      className={cn(
        "rounded-full border-0 bg-transparent p-3 transition-colors",
        hoverClass,
        className
      )}
    >
      <MenuIcon size={MENU_ICON_SIZE} />
    </button>
  );
}

type NavbarProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
};

// Module-level, not per-render closures: these are permanent no-ops (PricingAudit
// reads the selected property from Redux/URL), but PropertySelector's data-fetch
// effect depends on onPropertySelect's identity, so a stable reference is required
// to avoid refetching on every unrelated Navbar render.
const handlePropertySelect = (_propertyId: string) => {};

const handlePropertiesSelect = (_propertyIds: string[]) => {};

function Navbar({ onDrawerToggle }: NavbarProps) {
  const location = useLocation();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.values.sm}px)`);
  const isActiveLeads = location.pathname.startsWith("/active-leads");
  const isGpc = location.pathname.startsWith("/group-pricing-calculator");
  const isGprt = location.pathname.startsWith("/group-pricing-requests");
  const isPricingAudit = location.pathname.startsWith("/pricing-audit-tool");
  const isMultiSelectRoute = isActiveLeads || isGpc || isGprt || isPricingAudit;
  const multiSelectPage: MultiSelectPage = isActiveLeads
    ? "activeLeads"
    : isGprt
    ? "groupPricingRequests"
    : isPricingAudit
    ? "pricingAudit"
    : "groupPricingCalculator";
  const darkHeader = isMobile && isActiveLeads;
  // PropertySelector keeps per-page fetch/selection state in local refs/state
  // that only reset on mount. Now that Navbar persists across route changes
  // (sidebar collapse survives navigation), it no longer remounts on its own
  // when switching pages — so force a fresh instance via key whenever the
  // page/mode it represents changes; it re-reads the shared selection on mount.
  const propertySelectorKey = isMultiSelectRoute ? multiSelectPage : "single";
  const headerBackground = darkHeader ? SIDEBAR.background : HEADER.background;
  const headerColor = darkHeader ? "#fff" : HEADER.color;

  const hamburgerHoverClass = darkHeader
    ? "hover:bg-white/10"
    : "hover:bg-black/5";

  // Both branches below share the same tree shape (outer row div > inner row
  // div > [DrawerMenuButton, PropertySelector wrapper, ...]) all the way down
  // to PropertySelector, varying only in className/conditional siblings.
  // This matters: resizing across the mobile breakpoint mid-interaction (e.g.
  // an open, unapplied Active Leads multi-select dropdown) flips `darkHeader`
  // on every render, and React only preserves a component's state across a
  // conditional if it sees the same element type at the same position in its
  // parent's children on both sides of the switch — if the two branches were
  // shaped differently (as they used to be), PropertySelector would unmount
  // and remount here, silently discarding the very state PropertySelector's
  // own lifted dropdownOpen/pendingIds/stableProperties was built to survive.
  return (
    <header
      className={`sticky top-0 z-shellHeader ${
        darkHeader ? "" : "shadow-card"
      }`}
      style={{ background: headerBackground, color: headerColor }}
    >
      <div
        className={
          darkHeader
            ? "flex flex-col gap-[5px] pr-2 pt-[3px] pb-[5px]"
            : "flex min-h-[56px] w-full items-center px-4 sm:min-h-[64px]"
        }
      >
        <div className={darkHeader ? "flex items-center gap-0.5" : "contents"}>
          <DrawerMenuButton
            onClick={onDrawerToggle}
            hoverClass={hamburgerHoverClass}
            className={
              darkHeader
                ? "inline-flex items-center justify-center text-white"
                : "mr-1 block md:hidden"
            }
          />
          <div
            className={
              darkHeader
                ? "min-w-0 flex-1"
                : "ml-0 min-w-[65vw] md:-ml-2 md:min-w-0"
            }
          >
            {isMultiSelectRoute ? (
              <PropertySelector
                key={propertySelectorKey}
                multiSelect
                page={multiSelectPage}
                onPropertySelect={handlePropertiesSelect}
                darkBackground={darkHeader}
              />
            ) : (
              <PropertySelector
                key={propertySelectorKey}
                onPropertySelect={handlePropertySelect}
              />
            )}
          </div>
          {!darkHeader && <div className="flex-1" />}
          {!darkHeader && (
            <div className="flex shrink-0 items-center">
              <NotificationBell />
            </div>
          )}
        </div>
        {darkHeader && (
          <div className="px-3.5 pb-px">
            <p className="m-0 text-2xl font-bold leading-[1.2] tracking-[-0.01em] text-white">
              Active Leads
            </p>
            <p className="mt-px mb-0 text-xs font-normal text-white/55">
              Every lead your team is working — live from the CRM.
            </p>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navbar;
